import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { rotationForShapeState } from '../helpers/rotation-degrees'
import { rotationSliderTupleIfUnchanged } from '../helpers/slider-tuples'
import type { SingleRotateBefore } from '../types/single-rotate-before'

/** Rotation slider/text state, the refs that track an in-progress slider drag or text edit, and display setters. */
export function useRotationState(shapeManagerRef?: React.RefObject<ShapeManagerRef | null>) {
  const [rotation, setRotation] = useState([0])
  const rotationRef = useRef(rotation)

  rotationRef.current = rotation
  const [rotationText, setRotationText] = useState('0')
  /** While dragging the rotation slider, avoid syncing rotation from manager (stale ref / effect order). */
  const panelRotationSliderActiveRef = useRef(false)
  /** Captures pre-drag rotation so slider live preview can still commit a correct undo delta. */
  const panelSingleRotateBeforeRef = useRef<SingleRotateBefore | null>(null)
  /** Rotation value (degrees) when the text input was focused — used to revert on error. */
  const rotationEditStartValueRef = useRef<number | null>(null)

  const getMixedMultiSelectPanelRotationDeg = useCallback((): number => {
    const mgr = shapeManagerRef?.current
    const boxDeg = mgr?.getMultiSelectChromeRotationDeg?.() ?? null

    if (boxDeg != null && Number.isFinite(boxDeg)) {
      return rotationForShapeState(boxDeg)
    }

    return rotationForShapeState(rotationRef.current[0] ?? 0)
  }, [shapeManagerRef])

  /** Shows `deg` in the slider and text field, keeping the current tuple/text when unchanged. */
  const syncRotationDisplay = useCallback((deg: number) => {
    setRotation((prev) => rotationSliderTupleIfUnchanged(prev, deg))
    setRotationText((prev) => (prev === String(deg) ? prev : String(deg)))
  }, [])

  /** Puts `deg` into the slider (always a new tuple) and text field. */
  const setRotationDisplay = useCallback((deg: number) => {
    setRotation([deg])
    setRotationText(String(deg))
  }, [])

  return {
    rotation,
    rotationRef,
    rotationText,
    setRotationText,
    panelRotationSliderActiveRef,
    panelSingleRotateBeforeRef,
    rotationEditStartValueRef,
    getMixedMultiSelectPanelRotationDeg,
    syncRotationDisplay,
    setRotationDisplay,
  }
}
